import { message } from "antd"
import axios from "../../axios"
import config from "../../config"
import { businessInitialValues, usersInitialValues } from "../../constants/store-initial-values"

export const createAuthSlice = ((set, get) => ({
    auth: {
        loading: false,
        userToken: '',
        userInfo: {},
        ttl: null,
        returnUrl: "",
        // New auth flow properties
        mobile: '',
        tokenPassword: '',
        resendWaitSeconds: 120,
        otpLength: 4,
        message: '',
        hasPass: false,
        isRegistered: false,
    },

    // Authentication method for mobile number
    authentication: async (params) => {
        set(state => ({ auth: { ...state.auth, loading: true, message: '' } }));

        if (!params.MobileNumber) {
            message.error("لطفا شماره موبایل را وارد کنید");
            set(state => ({ auth: { ...state.auth, loading: false } }));
            return;
        }

        try {
            const resp = await axios({
                method: 'post',
                url: '/v1/authentication/Tools',
                baseURL: config.apiUrls(),
                data: {
                    MobileNumber: params.MobileNumber,
                },
                // Remove manual headers - axios interceptor will handle it
            });

            const data = resp?.data;
            set(state => ({
                auth: {
                    ...state.auth,
                    loading: false,
                    mobile: params.MobileNumber,
                    tokenPassword: data?.SecurityData,
                    resendWaitSeconds: data?.ResendWaitSeconds || 120,
                    otpLength: data?.OtpLength || 5,
                    hasPass: data?.HasPassword || false,
                    isRegistered: data?.Registered || false,
                    message: ''
                }
            }));

            return data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || "خطا در ارسال کد تایید";
            set(state => ({
                auth: {
                    ...state.auth,
                    loading: false,
                    message: errorMessage
                }
            }));
            message.error(errorMessage);
        }
    },

    // Updated login method to handle both OTP and Password
    login: async (params) => {
        set(state => ({ auth: { ...state.auth, loading: true, message: '' } }));

        // Handle old login format (username/password) - keep for backward compatibility
        if (params.username && params.password) {
            try {
                const resp = await axios({
                    method: 'post',
                    url: '/v1/authentication/login',
                    baseURL: config.apiUrls(),
                    data: {
                        Username: params.username,
                        Password: params.password,
                    },
                    // Remove manual headers - axios interceptor will handle it
                });
                set(state => ({ auth: { ...state.auth, userToken: resp?.data?.JWT, loading: false } }));
                return resp?.data;
            } catch (error) {
                get().logOut();
                set(state => ({ auth: { ...state.auth, loading: false } }));
                message.error(error.response?.data?.message || "خطا در ورود به سیستم");
                return;
            }
        }

        // Handle new login format (OTP or Password with SecurityData token)
        if (!params.tokenPassword) {
            message.error("خطا در فرآیند احراز هویت");
            set(state => ({ auth: { ...state.auth, loading: false } }));
            return;
        }

        try {
            const loginData = {
                SecurityData: params.tokenPassword,
            };

            // Add either Code or Password
            if (params.Code) {
                loginData.Code = params.Code;
            } else if (params.Password) {
                loginData.Password = params.Password;
            } else {
                message.error("لطفا کد تایید یا رمز عبور را وارد کنید");
                set(state => ({ auth: { ...state.auth, loading: false } }));
                return;
            }

            const resp = await axios({
                method: 'post',
                url: '/v1/authentication/login',
                baseURL: config.apiUrls(),
                data: loginData,
            });

            set(state => ({
                auth: {
                    ...state.auth,
                    userToken: resp?.data?.JWT,
                    loading: false,
                    message: ''
                }
            }));
            return resp?.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || "خطا در ورود به سیستم";
            set(state => ({
                auth: {
                    ...state.auth,
                    loading: false,
                    message: errorMessage
                }
            }));
            message.error(errorMessage);
        }
    },

    getUserInfo: async () => {
        set(state => ({ auth: { ...state.auth, loading: true } }))
        try {
            const res = await axios({
                method: 'get',
                baseURL: config.apiUrls(),
                url: '/v1/user/info',
                // Remove manual headers - axios interceptor will handle it
            })
            set(state => ({ auth: { ...state.auth, userInfo: res.data, loading: false } }))
            return res.data
        } catch (error) {
            set(state => ({ auth: { ...state.auth, loading: false } }))
            get().logOut()
        }
    },

    checkUserTTl: async (params) => {
        try {
            const res = await axios({
                method: 'post',
                baseURL: config.apiUrls(),
                url: '/v1/user/CheckUserToken',
            })
            return res
        } catch (error) {
            set(state => ({ auth: { ...state.auth, loading: false } }))
            if (params === "/" || params.includes("/auth")) {
                get().logOut()
            } else {
                get().logOut()
            }
            return error.response.data
        }
    },

    setReturnUrl: (url) => set(state => ({ auth: { ...state.auth, returnUrl: url } })),
    clearAuthMessage: () => set(state => ({ auth: { ...state.auth, message: '' } })),
    clearLoginInfo: () => set(state => ({
        auth: {
            ...state.auth,
            mobile: '',
            tokenPassword: '',
            message: '',
            hasPass: false,
            isRegistered: false
        }
    })),

    LoginSuccess: (userToken) => set(state => ({ auth: { ...state.auth, userToken } })),
    UserInfo: (userInfo) => set(state => ({ auth: { ...state.auth, userInfo } })),

    logOut: () => {
        set(state => ({
            auth: {
                ...state.auth,
                loading: false,
                userToken: '',
                userInfo: {},
                ttl: null,
                mobile: '',
                tokenPassword: '',
                message: '',
                hasPass: false,
                isRegistered: false
            }
        }));
        if (!window.location.pathname.includes("/auth")) {
            setTimeout(() => {
                window.location.href = `${window.location.origin}/auth/login?returnUrl=${window.location.pathname}`
            }, 3000);
        }
    },

    AuthLoading: (loading) => set(state => ({ auth: { ...state.auth, loading } })),

    TtlUpdator: () => {
        const minutesToAdd = 30.1
        const futureDate = new Date(Date.now() + minutesToAdd * 60000)
        set(state => ({ auth: { ...state.auth, ttl: futureDate.getTime() } }))
    },

    RESET_APP: () => set((state) => ({
        business: businessInitialValues,
        users: usersInitialValues,
        auth: {
            ...state.auth,
            mobile: '',
            tokenPassword: '',
            message: '',
            hasPass: false,
            isRegistered: false
        }
    })),
}))
